import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    CheckCircle, Users, Clock, TrendingUp, LogOut,
    AlertCircle, RefreshCw, User, Plus, X, Calendar,
    ChevronLeft, ChevronRight, FileText, Sparkles,
    BookOpen, Award, Target, Lightbulb, PanelLeftClose, PanelLeft
} from 'lucide-react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import AcademicWorkspace from '../AcademicWorkspace';
import { useAuth } from '../../services/AuthContext';
import * as api from '../../services/api';
import './InstructorDashboard.css';
import AdminSessionReport from './AdminSessionReport';

const PAGE_SIZE = 10;

/* ── Motivational Slider Data ──────────────────────────────── */
const SLIDES = [
    {
        headline: 'إحنا مبسوطين بوجودك معانا في كود ويف',
        subtitle: 'دورك كإنستراكتور مش مجرد شرح كورس، إنت بتبني جيل جديد من المبرمجين.',
        className: 'id-slide--1',
    },
    {
        headline: 'إحنا مبسوطين بوجودك معانا في كود ويف',
        subtitle: 'كل ساعة بتقضيها مع الطلاب بتفرق في مستقبلهم المهني.',
        className: 'id-slide--2',
    },
    {
        headline: 'إحنا مبسوطين بوجودك معانا في كود ويف',
        subtitle: 'استمرارك في التعليم هو السبب الحقيقي في نجاح طلاب كتير.',
        className: 'id-slide--3',
    },
    {
        headline: 'إحنا مبسوطين بوجودك معانا في كود ويف',
        subtitle: 'المعلم المميز هو اللي بيسيب أثر حقيقي في طلابه.',
        className: 'id-slide--4',
    },
];

/* ── Reusable StatCard Component ───────────────────────────── */
const StatCard = React.memo(function StatCard({ icon: Icon, val, label, color, bg, unit = '' }) {
    return (
        <div className="id-stat id-animate-in">
            <div className="id-stat-icon" style={{ background: bg }}>
                <Icon size={20} color={color} />
            </div>
            <div>
                <div className="id-stat-val" style={{ color }}>{val}{unit}</div>
                <div className="id-stat-label">{label}</div>
            </div>
        </div>
    );
});

/* ── Motivational Slider Component ─────────────────────────── */
const MotivationalSlider = React.memo(function MotivationalSlider() {
    const [current, setCurrent] = useState(0);
    const intervalRef = useRef(null);

    const startAutoPlay = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setCurrent(prev => (prev + 1) % SLIDES.length);
        }, 5000);
    }, []);

    useEffect(() => {
        startAutoPlay();
        return () => clearInterval(intervalRef.current);
    }, [startAutoPlay]);

    const goTo = useCallback((idx) => {
        setCurrent(idx);
        startAutoPlay();
    }, [startAutoPlay]);

    const prev = useCallback(() => {
        setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length);
        startAutoPlay();
    }, [startAutoPlay]);

    const next = useCallback(() => {
        setCurrent(c => (c + 1) % SLIDES.length);
        startAutoPlay();
    }, [startAutoPlay]);

    return (
        <div className="id-slider id-animate-in" dir="rtl">
            <div
                className="id-slider-track"
                style={{ transform: `translateX(${current * 100}%)` }}
            >
                {SLIDES.map((slide, i) => (
                    <div key={i} className={`id-slide ${slide.className}`}>
                        <div className="id-slide-content-wrapper">
                            <h3 className="id-slide-headline">{slide.headline}</h3>
                            <p className="id-slide-subtitle">{slide.subtitle}</p>
                        </div>
                    </div>
                ))}
            </div>
            <div className="id-slider-dots">
                {SLIDES.map((_, i) => (
                    <button
                        key={i}
                        className={`id-slider-dot${i === current ? ' active' : ''}`}
                        onClick={() => goTo(i)}
                        aria-label={`Go to slide ${i + 1}`}
                    />
                ))}
            </div>
            <button className="id-slider-arrow id-slider-arrow--prev" onClick={prev} aria-label="Previous slide">
                <ChevronRight size={18} />
            </button>
            <button className="id-slider-arrow id-slider-arrow--next" onClick={next} aria-label="Next slide">
                <ChevronLeft size={18} />
            </button>
        </div>
    );
});

/* ── Student Row Component (Desktop Table) ─────────────────── */
const StudentRow = React.memo(function StudentRow({ student, onOpen }) {
    const rem = student.remainingHours || 0;
    return (
        <tr>
            <td>
                <div className="id-cell-name">
                    <div className="id-cell-avatar">{student.fullName?.[0]}</div>
                    <div>
                        <div className="id-cell-fullname">{student.fullName}</div>
                        <div className="id-cell-email">{student.email}</div>
                    </div>
                </div>
            </td>
            <td>
                <span className={`id-track id-track--${student.track || 'web'}`}>
                    {student.track || '—'}
                </span>
            </td>
            <td><strong>{student.totalHoursAllowed || 0}h</strong></td>
            <td>
                <span className={`id-hours-badge ${rem <= 0 ? 'id-hours--red' : rem <= 2 ? 'id-hours--yellow' : 'id-hours--green'}`}>
                    {rem.toFixed(1)}h
                </span>
            </td>
            <td style={{ textAlign: 'right' }}>
                <button className="id-open-btn" onClick={() => onOpen(student)}>Open</button>
            </td>
        </tr>
    );
});

/* ── Student Card Component (Mobile) ───────────────────────── */
const StudentCard = React.memo(function StudentCard({ student, onOpen }) {
    const rem = student.remainingHours || 0;
    return (
        <div className="id-student-card">
            <div className="id-cell-avatar">{student.fullName?.[0]}</div>
            <div className="id-student-card-info">
                <div className="id-student-card-name">{student.fullName}</div>
                <div className="id-student-card-email">{student.email}</div>
                <div className="id-student-card-meta">
                    <span className={`id-track id-track--${student.track || 'web'}`}>{student.track || '—'}</span>
                    <span className={`id-hours-badge ${rem <= 0 ? 'id-hours--red' : rem <= 2 ? 'id-hours--yellow' : 'id-hours--green'}`}>
                        {rem.toFixed(1)}h left
                    </span>
                </div>
            </div>
            <button className="id-open-btn" onClick={() => onOpen(student)}>Open</button>
        </div>
    );
});

/* ── Student Modal Component ───────────────────────────────── */
const StudentModal = React.memo(function StudentModal({ modal, form, setForm, onSave, onClose, saving }) {
    if (!modal.open || !modal.student) return null;
    const s = modal.student;
    const rem = s.remainingHours || 0;

    return (
        <div className="id-overlay" onClick={onClose}>
            <div className="id-modal" onClick={e => e.stopPropagation()}>
                <div className="id-modal-scrollable">
                    <div className="id-modal-head">
                        <div>
                            <h3 className="id-modal-title">{s.fullName}</h3>
                            <p className="id-modal-email">{s.email}</p>
                        </div>
                        <button className="id-modal-x" onClick={onClose}><X size={18} /></button>
                    </div>

                    {/* Hours Summary */}
                    <div className="id-modal-stats">
                        <div className="id-ms-card" style={{ borderLeft: '3px solid #2563eb' }}>
                            <span className="id-ms-label">Total</span>
                            <span className="id-ms-val">{s.totalHoursAllowed || 0}h</span>
                        </div>
                        <div className="id-ms-card" style={{ borderLeft: '3px solid #f59e0b' }}>
                            <span className="id-ms-label">Consumed</span>
                            <span className="id-ms-val">{s.consumedHours || 0}h</span>
                        </div>
                        <div className="id-ms-card" style={{ borderLeft: `3px solid ${rem <= 0 ? '#dc2626' : '#16a34a'}` }}>
                            <span className="id-ms-label">Remaining</span>
                            <span className="id-ms-val">{rem.toFixed(1)}h</span>
                        </div>
                    </div>

                    {/* Session History */}
                    <div className="id-modal-section-title">Session History</div>
                    <div className="id-modal-table-wrap">
                        {modal.sessLoading ? (
                            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
                        ) : modal.sessions.length === 0 ? (
                            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>No sessions recorded yet.</div>
                        ) : (
                            <table className="id-table id-table--compact">
                                <thead><tr><th>Date</th><th>Duration</th><th>Notes</th></tr></thead>
                                <tbody>
                                    {modal.sessions.map((ses, i) => (
                                        <tr key={i}>
                                            <td>{new Date(ses.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                            <td><span className="id-dur-pill">{ses.durationInHours}h</span></td>
                                            <td style={{ color: '#64748b', fontSize: '0.78rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ses.notes || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Attendance Form */}
                    {rem <= 0 ? (
                        <div className="id-modal-warning">
                            <AlertCircle size={15} /> No sessions remaining — contact admin
                        </div>
                    ) : (
                        <form className="id-modal-form" id="session-form" onSubmit={onSave}>
                            <div className="id-modal-section-title" style={{ margin: '0 0 0.5rem' }}>Record Session</div>
                            <div className="id-form-row">
                                <div className="id-form-group">
                                    <label>Date</label>
                                    <input type="date" value={form.date} max={todayStr()} onChange={e => setForm({ ...form, date: e.target.value })} required />
                                </div>
                                <div className="id-form-group">
                                    <label>Hours</label>
                                    <input type="number" step="0.1" min="0.1" max={rem} value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} required />
                                </div>
                            </div>
                            <div className="id-form-group">
                                <label>Notes <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                                <input type="text" placeholder="Topics covered..." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                            </div>
                        </form>
                    )}
                </div>

                {/* Sticky Save Button */}
                {rem > 0 && (
                    <div className="id-modal-save-wrap">
                        <button type="submit" form="session-form" className="id-save-btn" disabled={saving}>
                            {saving ? 'Saving...' : <><Plus size={15} /> Save Session</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

/* ── Main Dashboard Component ──────────────────────────────── */
export default function InstructorDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    const [dashData, setDashData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [page, setPage] = useState(0);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Modal state
    const [modal, setModal] = useState({ open: false, student: null, sessions: [], sessLoading: false });
    const [form, setForm] = useState({ duration: 1, date: todayStr(), notes: '' });
    const [saving, setSaving] = useState(false);

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.getInstructorDashboard();
            setDashData(res.data);
        } catch {
            setError('Failed to load dashboard.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    // Auto-dismiss success
    useEffect(() => {
        if (successMsg) {
            const t = setTimeout(() => setSuccessMsg(''), 3500);
            return () => clearTimeout(t);
        }
    }, [successMsg]);

    // Open student modal
    const openStudent = useCallback(async (student) => {
        setModal({ open: true, student, sessions: [], sessLoading: true });
        setForm({ duration: 1, date: todayStr(), notes: '' });
        try {
            const res = await api.getStudentSessions(student._id);
            setModal(m => ({ ...m, sessions: res.data?.sessions || [], sessLoading: false }));
        } catch {
            setModal(m => ({ ...m, sessLoading: false }));
        }
    }, []);

    const closeModal = useCallback(() => {
        setModal({ open: false, student: null, sessions: [], sessLoading: false });
    }, []);

    // Save session
    const handleSave = useCallback(async (e) => {
        e.preventDefault();
        const s = modal.student;
        if (!s) return;
        const dur = parseFloat(form.duration);
        if (isNaN(dur) || dur <= 0) { setError('Enter valid duration'); return; }
        if (s.remainingHours < dur) { setError(`Only ${s.remainingHours.toFixed(1)}h left`); return; }
        setSaving(true);
        try {
            await api.markStudentPresent(s._id, dur, form.date, form.notes);
            setSuccessMsg(`✓ Saved ${dur}h for ${s.fullName}`);
            closeModal();
            await fetchDashboard();
        } catch (err) {
            setError(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    }, [modal.student, form, closeModal, fetchDashboard]);

    // Memoize derived data
    const stats = useMemo(() => dashData?.stats || {}, [dashData]);
    const students = useMemo(() => dashData?.students || [], [dashData]);
    const totalPages = Math.ceil(students.length / PAGE_SIZE);
    const paged = useMemo(() => students.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [students, page]);

    if (loading && location.pathname !== '/instructor/session-report') {
        return (
            <div className="id-loading">
                <div className="id-spinner" />
                <p>Loading dashboard...</p>
            </div>
        );
    }

    const handleLogout = () => { logout(); navigate('/login'); };

    return (
        <div className="id-root">
            {/* Toasts */}
            {successMsg && <div className="id-toast id-toast-ok"><CheckCircle size={18} /> {successMsg}</div>}
            {error && <div className="id-toast id-toast-err" onClick={() => setError('')}><AlertCircle size={18} /> {error}</div>}

            {/* ── Sidebar ──────────────────────────────────────── */}
            <aside className={`id-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
                <button
                    className="id-collapse-btn"
                    onClick={() => setSidebarCollapsed(c => !c)}
                    title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {sidebarCollapsed ? <PanelLeft size={14} /> : <PanelLeftClose size={14} />}
                </button>

                <div className="id-brand">CODE<span>WAVE</span></div>
                <div className="id-brand-sub">INSTRUCTOR PORTAL</div>

                <nav className="id-nav">
                    <NavLink to="/instructor" end className={({ isActive }) => isActive ? "id-nav-link active" : "id-nav-link"}>
                        <Users size={20} /><span>Dashboard</span>
                    </NavLink>
                    <NavLink to="/instructor/session-report" className={({ isActive }) => isActive ? "id-nav-link active" : "id-nav-link"}>
                        <FileText size={20} /><span>Session Report</span>
                    </NavLink>
                    <NavLink to="/instructor/academics" className={({ isActive }) => isActive ? "id-nav-link active" : "id-nav-link"}>
                        <BookOpen size={18} /> Academics
                    </NavLink>
                </nav>

                <div className="id-sidebar-spacer" />

                <div className="id-user-card">
                    <div className="id-avatar">{user?.fullName?.[0] || 'I'}</div>
                    <div className="id-user-info">
                        <div className="id-user-name">{user?.fullName}</div>
                        <div className="id-user-email">{user?.email}</div>
                    </div>
                </div>

                <button className="id-logout-btn" onClick={handleLogout}>
                    <LogOut size={18} /><span>Sign Out</span>
                </button>
            </aside>

            {/* ── Main Area ────────────────────────────────────── */}
            <main className="id-main">
                {/* Mobile Top Bar */}
                <div className="id-mobile-topbar">
                    <div className="id-mobile-brand">CODE<span>WAVE</span></div>
                    <button className="id-mobile-logout" onClick={handleLogout}>
                        <LogOut size={18} />
                    </button>
                </div>

                <header className="id-topbar">
                    <div>
                        <h1 className="id-title">
                            {location.pathname === '/instructor/session-report' ? 'Session Report' : 'Attendance Dashboard'}
                        </h1>
                        <p className="id-subtitle">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                    </div>
                    {location.pathname !== '/instructor/session-report' && (
                        <button className="id-refresh-btn" onClick={fetchDashboard}>
                            <RefreshCw size={16} /> <span>Refresh</span>
                        </button>
                    )}
                </header>

                {/* Scrollable Content */}
                <div className="id-scroll-area">
                    <div className="id-content">
                        {location.pathname === '/instructor/academics' ? (
                            <AcademicWorkspace compact defaultTab="overview" />
                        ) : location.pathname === '/instructor/session-report' ? (
                            <AdminSessionReport />
                        ) : (
                            <>
                                {/* Motivational Slider */}
                                <MotivationalSlider />

                                {/* Stats */}
                                <div className="id-stats">
                                    <StatCard icon={Users} val={stats.totalStudents ?? 0} label="Students" color="#2563eb" bg="#dbeafe" />
                                    <StatCard icon={Clock} val={stats.totalHours ?? 0} label="Total Hours" color="#9333ea" bg="#f3e8ff" unit="h" />
                                    <StatCard icon={TrendingUp} val={stats.hoursThisMonth ?? 0} label="This Month" color="#16a34a" bg="#dcfce7" unit="h" />
                                </div>

                                {/* Student List */}
                                <div className="id-table-card id-animate-in">
                                    <div className="id-table-header">
                                        <h2><User size={18} /> Student Roster</h2>
                                        <span className="id-badge">{students.length} students</span>
                                    </div>

                                    {students.length === 0 ? (
                                        <div className="id-empty">
                                            <Users size={48} style={{ opacity: 0.2 }} />
                                            <p>No students assigned yet.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Desktop Table */}
                                            <div className="id-table-scroll">
                                                <table className="id-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Student</th>
                                                            <th>Track</th>
                                                            <th>Total</th>
                                                            <th>Remaining</th>
                                                            <th style={{ textAlign: 'right' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paged.map(s => (
                                                            <StudentRow key={s._id} student={s} onOpen={openStudent} />
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Mobile Cards */}
                                            <div className="id-student-cards">
                                                {paged.map(s => (
                                                    <StudentCard key={s._id} student={s} onOpen={openStudent} />
                                                ))}
                                            </div>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <div className="id-pagination">
                                                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                                                        <ChevronLeft size={16} />
                                                    </button>
                                                    <span>Page {page + 1} / {totalPages}</span>
                                                    <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>

            {/* ── Student Modal ────────────────────────────────── */}
            <StudentModal
                modal={modal}
                form={form}
                setForm={setForm}
                onSave={handleSave}
                onClose={closeModal}
                saving={saving}
            />
        </div>
    );
}

function todayStr() {
    return new Date().toISOString().split('T')[0];
}
